import type { FC, ReactNode } from 'react'

import { useEffect } from 'react'
import { animate, useMotionValue, usePresence } from 'motion/react'
import { easeInOutQuart, easeOutExpo } from '@/lib/motion'
import { Iris, IRIS_OPEN } from '@/components/motion/iris'

interface PageTransitionProps {
    children: ReactNode
    arrival?: boolean
}

export const PageTransition: FC<PageTransitionProps> = ({ children, arrival = false }): ReactNode => {
    const [isPresent, safeToRemove] = usePresence()
    const aperture = useMotionValue(arrival ? IRIS_OPEN : 0)

    useEffect(() => {
        if (arrival) return

        const controls = animate(aperture, IRIS_OPEN, { duration: 1, ease: easeOutExpo, delay: 0.12 })

        return () => controls.stop()
        // Opens once, when the page arrives.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (isPresent) return

        const controls = animate(aperture, 0, { duration: 0.55, ease: easeInOutQuart })

        controls.finished.then(() => safeToRemove?.())

        return () => controls.stop()
    }, [isPresent, aperture, safeToRemove])

    return (
        <div>
            <div aria-hidden='true' className='pointer-events-none fixed inset-0 z-[110]'>
                <Iris aperture={aperture} />
            </div>

            {children}
        </div>
    )
}
